import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { plotDossiersApi } from '../../api/plotDossiers';
import { usePaidConfirm } from '../Common/usePaidConfirm';
import { parseStoryFrameworkRef, resolveStoryFrameworkMeta, storyFrameworkRef } from '../../api/frameworks';
import {
  DOSSIER_AUDIT_TIP,
  DOSSIER_FRAMEWORK_HAVE,
  DOSSIER_FRAMEWORK_SELECT_HINT,
} from '../../utils/plotReadyCopy';
import { outlineProgressLine } from '../../utils/bookPathTip';
import {
  BRING_A_TURN,
  BRING_A_TURN_WHY,
  CHOOSE_A_CHAT_LINE,
  CONFIRM_TURN,
  LOGLINE_EMPTY,
  PENDING_TURNS_WHY,
  PROPOSE_TURNS,
  REGENERATE_ALL_TURNS,
  TURN_PENDING,
  bringTurnEmptyWhy,
  liftedLogline,
  unusedChatLines,
  writerFacingError,
} from '../../utils/plotTurns';
import FrameworkPicker from '../Frameworks/FrameworkPicker';
import { handmadeSequenceCount } from '../../utils/outlineCardRows';
import GenreStandingMark from '../Frameworks/GenreStandingMark';
import ThinGenreMarker from '../Frameworks/ThinGenreMarker';
import AtlasIcon from '../icons/AtlasIcon';
import { TextSizeStepper } from '../Common/TextSizeStepper';
import { PLOT_TEXT_SIZE, readStoredPercent } from '../../utils/textSizeRange';
import { useSourceViewer } from '../DocumentImport/SourceViewerContext';
import { citationShortId } from '../DocumentImport/importCitation';
import '../Entities/CharacterDossier.css';
import '../BookSheet/BookSheet.css';
import './PlotDossier.css';

/** Plot Dossier window only. Default is 1.5× today's type; not app zoom. */
export const PD_ZOOM = PLOT_TEXT_SIZE;
const PD_ZOOM_KEY = 'brainstormy.plotDossier.zoom';

export function readPlotDossierZoom() {
  try {
    return readStoredPercent(localStorage.getItem(PD_ZOOM_KEY), PD_ZOOM);
  } catch {
    /* private mode */
    return PD_ZOOM.default;
  }
}

/**
 * Plot Dossier v2 (#596) — plot-native threads, event+consequence offers,
 * altitude, framework ownership, audit → Plot Focus, gap detection,
 * hierarchical mapping. Not Character Dossier's offer row.
 */

const ALTITUDES = [
  { id: 'act', label: 'Act' },
  { id: 'sequence', label: 'Sequence' },
  { id: 'scene', label: 'Scene' },
  { id: 'beat', label: 'Beat' },
];

function emptyView(storyId) {
  return {
    story: { id: storyId },
    threads: [{ id: 'local-main', kind: 'main', name: 'Main plot', entries: [] }],
    rollup_text: null,
    mapping_status: null,
    structure_linked: false,
    acts: [],
    sequences: [],
    scenes: [],
    framework_ref: null,
    framework_drift: false,
    gap_fill_enabled: false,
    gaps: [],
    audit: null,
    logline: null,
    unused_chat_lines: [],
  };
}

function questionText(item) {
  if (item == null) return '';
  if (typeof item === 'string') return item.trim();
  if (typeof item === 'object') {
    const text = item.text || item.question || item.content || item.label;
    return typeof text === 'string' ? text.trim() : '';
  }
  return String(item).trim();
}

/** Staging rows may store a JSON string or object instead of a JSON array. */
export function coerceAuditQuestions(value) {
  if (Array.isArray(value)) {
    return value.map(questionText).filter(Boolean);
  }
  if (typeof value === 'string') {
    const text = value.trim();
    if (!text) return [];
    if (text.startsWith('[') || text.startsWith('{')) {
      try {
        return coerceAuditQuestions(JSON.parse(text));
      } catch {
        return [text];
      }
    }
    return [text];
  }
  if (value && typeof value === 'object') {
    if (Object.prototype.hasOwnProperty.call(value, 'questions')) {
      return coerceAuditQuestions(value.questions);
    }
    return Object.values(value).map(questionText).filter(Boolean);
  }
  return [];
}

function altitudeLabel(value) {
  return ALTITUDES.find((a) => a.id === value)?.label || value || 'Scene';
}

export default function PlotDossier({ storyId }) {
  const navigate = useNavigate();
  const { openImportChunkCitation } = useSourceViewer();
  const [data, setData] = useState(undefined);
  const [error, setError] = useState(null);
  const [generating, setGenerating] = useState(false);
  const { ask: askPaid, dialog: paidDialog } = usePaidConfirm();
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);
  const [expandedProv, setExpandedProv] = useState({});
  const [generatedEmpty, setGeneratedEmpty] = useState(false);
  const [activeThreadId, setActiveThreadId] = useState(null);
  const [addingSubplot, setAddingSubplot] = useState(false);
  const [subplotName, setSubplotName] = useState('');
  const [frameworkPickerOpen, setFrameworkPickerOpen] = useState(false);
  const [frameworkLabel, setFrameworkLabel] = useState(null);
  const [frameworkGenre, setFrameworkGenre] = useState(null);
  const [frameworkThin, setFrameworkThin] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [discardAllConfirm, setDiscardAllConfirm] = useState(false);
  const [assigning, setAssigning] = useState(null);
  const [auditBusy, setAuditBusy] = useState(false);
  const [typeZoom, setTypeZoom] = useState(readPlotDossierZoom);
  const [pickingTurn, setPickingTurn] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(PD_ZOOM_KEY, String(typeZoom));
    } catch {
      /* private mode */
    }
  }, [typeZoom]);

  const load = useCallback(async () => {
    setError(null);
    try {
      const res = await plotDossiersApi.get(storyId);
      setData(res || emptyView(storyId));
      const threads = res?.threads || [];
      if ((threads.flatMap((t) => t.entries || [])).length > 0) setGeneratedEmpty(false);
      if (!activeThreadId && threads[0]?.id) setActiveThreadId(threads[0].id);
    } catch (e) {
      const generic = /internal error/i.test(e.message || '');
      setError(generic ? null : (e.message || 'Failed to load plot dossier'));
      setData(emptyView(storyId));
    }
  }, [storyId, activeThreadId]);

  useEffect(() => {
    setData(undefined);
    setGeneratedEmpty(false);
    setActiveThreadId(null);
    setSelectedIds(new Set());
    setPickingTurn(false);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storyId]);

  useEffect(() => {
    const ref = data?.framework_ref;
    if (!ref) {
      setFrameworkLabel(null);
      setFrameworkGenre(null);
      setFrameworkThin(false);
      return undefined;
    }
    let cancelled = false;
    Promise.resolve(resolveStoryFrameworkMeta(ref)).then((meta) => {
      if (!cancelled) {
        setFrameworkLabel(meta?.framework_name || null);
        setFrameworkGenre(meta?.genre_tag || null);
        setFrameworkThin(Boolean(meta?.thin_genre));
      }
    });
    return () => { cancelled = true; };
  }, [data?.framework_ref]);

  const threads = data?.threads || [];
  const activeThread = threads.find((t) => t.id === activeThreadId) || threads[0];
  const realThreadId = activeThread && !String(activeThread.id).startsWith('local-')
    ? activeThread.id : null;
  const allEntries = useMemo(
    () => threads.flatMap((t) => t.entries || []),
    [threads],
  );
  const confirmedAll = useMemo(
    () => allEntries.filter((e) => e.status === 'confirmed' && !e.discarded),
    [allEntries],
  );
  const proposedHere = useMemo(
    () => (activeThread?.entries || []).filter((e) => e.status === 'proposed' && !e.discarded),
    [activeThread],
  );
  const discardedHere = useMemo(
    () => (activeThread?.entries || []).filter((e) => e.status === 'proposed' && e.discarded),
    [activeThread],
  );
  const unclean = useMemo(
    () => confirmedAll.filter((e) => e.mapping_unclean || (data?.mapping_status === 'needs_resolution' && !e.outline_section_id && e.altitude !== 'beat')),
    [confirmedAll, data?.mapping_status],
  );
  const collisions = useMemo(
    () => confirmedAll.filter((e) => e.collision),
    [confirmedAll],
  );
  const structureLinked = !!(data?.structure_linked) && (data?.acts || []).length > 0;
  const missingAct = unclean.some((e) => e.altitude === 'act') && !(data?.acts || []).length;
  const missingSequence = unclean.some((e) => e.altitude === 'sequence') && !(data?.sequences || []).length;

  const runExtract = async (mode, entryId = null) => {
    const ok = await askPaid('plot_extract');
    if (!ok) return;
    setGenerating(true);
    setError(null);
    try {
      const res = await plotDossiersApi.extract(storyId, {
        threadId: realThreadId,
        mode,
        entryId,
        gapFill: data?.gap_fill_enabled || null,
      });
      setGeneratedEmpty((res?.proposed || []).length === 0 && mode === 'generate');
      await load();
    } catch (e) {
      setError(writerFacingError(e.message || 'Could not bring a turn here.'));
    } finally {
      setGenerating(false);
    }
  };

  const handleConfirm = async (entry) => {
    if (!entry.altitude) {
      setError('Classify this entry as Act, Sequence, Scene, or Beat before confirming');
      return;
    }
    setBusy(true);
    try {
      await plotDossiersApi.confirmThread(storyId, [entry.id], { [entry.id]: entry.altitude });
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!editing) return;
    setBusy(true);
    try {
      await plotDossiersApi.editThreadEntry(editing.id, editing.draft);
      setEditing(null);
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const handleDiscard = async (ids) => {
    if (!ids.length) return;
    setBusy(true);
    try {
      await plotDossiersApi.discardThread(storyId, { entryIds: ids });
      setSelectedIds(new Set());
      setDiscardAllConfirm(false);
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const handleDiscardAll = async () => {
    if (!discardAllConfirm) {
      setDiscardAllConfirm(true);
      return;
    }
    setBusy(true);
    try {
      await plotDossiersApi.discardThread(storyId, { threadId: realThreadId, all: true });
      setDiscardAllConfirm(false);
      setSelectedIds(new Set());
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const handleAddSubplot = async () => {
    const name = subplotName.trim();
    if (!name) return;
    setBusy(true);
    try {
      const thread = await plotDossiersApi.addThread(storyId, name);
      setAddingSubplot(false);
      setSubplotName('');
      setActiveThreadId(thread.id);
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const openBringChoice = () => {
    setError(null);
    setPickingTurn(true);
    setGeneratedEmpty(unusedChatLines(data).length === 0);
  };

  const pickChatLine = async (messageId) => {
    const ok = await askPaid('plot_extract');
    if (!ok) return;
    setGenerating(true);
    setError(null);
    try {
      const res = await plotDossiersApi.extract(storyId, {
        threadId: realThreadId,
        mode: 'generate',
        sourceMessageId: messageId,
        gapFill: data?.gap_fill_enabled || null,
      });
      setGeneratedEmpty((res?.proposed || []).length === 0);
      setPickingTurn(false);
      await load();
    } catch (e) {
      setError(writerFacingError(e.message || 'Could not bring a turn here.'));
    } finally {
      setGenerating(false);
    }
  };

  const handleSelectFramework = async (dto, choice = {}) => {
    setBusy(true);
    const keepParts = Boolean(choice.keepParts);
    try {
      if (keepParts) {
        await plotDossiersApi.setFramework(storyId, storyFrameworkRef(dto), { keepParts: true });
      } else {
        await plotDossiersApi.setFramework(storyId, storyFrameworkRef(dto));
      }
      setFrameworkPickerOpen(false);
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const handleFrameworkNone = async () => {
    setBusy(true);
    try {
      await plotDossiersApi.setFramework(storyId, null);
      setFrameworkPickerOpen(false);
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const handleAudit = async () => {
    setAuditBusy(true);
    setError(null);
    try {
      await plotDossiersApi.audit(storyId);
      await load();
    } catch (e) {
      setError(e.message || 'Audit failed');
    } finally {
      setAuditBusy(false);
    }
  };

  const handleOpenFocus = async () => {
    setAuditBusy(true);
    setError(null);
    try {
      const res = await plotDossiersApi.openAuditFocus(storyId);
      if (res?.session_id) navigate(`/chat/${res.session_id}`);
      else await load();
    } catch (e) {
      setError(e.message || "Could not open the session about the book's argument");
    } finally {
      setAuditBusy(false);
    }
  };

  const handleAssign = async (entryId, altitude, outlineSectionId) => {
    setBusy(true);
    try {
      await plotDossiersApi.assignThreadEntry(storyId, entryId, { altitude, outlineSectionId });
      setAssigning(null);
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (entryId) => {
    setBusy(true);
    try {
      await plotDossiersApi.deleteThreadEntry(storyId, entryId);
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const toggleSelected = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const setEntryAltitude = (entryId, altitude) => {
    setData((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        threads: (prev.threads || []).map((t) => ({
          ...t,
          entries: (t.entries || []).map((e) => (e.id === entryId ? { ...e, altitude } : e)),
        })),
      };
    });
  };

  const targets = useMemo(() => ([
    ...(data?.acts || []).map((s) => ({ ...s, kind: 'act' })),
    ...(data?.sequences || []).map((s) => ({ ...s, kind: 'sequence' })),
    ...(data?.scenes || []).map((s) => ({ ...s, kind: 'scene' })),
  ]), [data]);

  const renderEntry = (entry) => {
    const proposed = entry.status === 'proposed' && !entry.discarded;
    const isEditing = editing && editing.id === entry.id;
    const seq = entry.sequence;
    return (
      <div
        key={entry.id}
        className={`cd-entry ${proposed ? 'cd-entry--proposed' : 'cd-entry--confirmed'}`}
      >
        <div className="cd-entry__head">
          {proposed && (
            <label className="pd-check">
              <input
                type="checkbox"
                checked={selectedIds.has(entry.id)}
                onChange={() => toggleSelected(entry.id)}
                aria-label={`Select ${entry.content || 'offer'}`}
              />
            </label>
          )}
          {seq != null && <span className="pd-seq">NO. {String(seq).padStart(2, '0')}</span>}
          <span className={`cd-entry__badge cd-entry__badge--${proposed ? 'proposed' : 'confirmed'}`}>
            {proposed ? TURN_PENDING : 'Confirmed'}
          </span>
          {entry.altitude && (
            <span className="cd-entry__badge">{altitudeLabel(entry.altitude)}</span>
          )}
          {entry.framework_beat_name && (
            <span className="cd-entry__badge pd-beat">{entry.framework_beat_name}</span>
          )}
          {entry.invented && (
            <span className="cd-entry__badge pd-invented">Invented</span>
          )}
          {entry.collision && (
            <span className="cd-entry__badge cd-entry__badge--conflict">Collision</span>
          )}
          {entry.drifted && <span className="cd-entry__badge cd-entry__badge--conflict">Source changed</span>}
        </div>
        {isEditing ? (
          <div className="cd-entry__edit">
            <textarea
              className="cd-entry__textarea"
              value={editing.draft}
              onChange={(e) => setEditing({ ...editing, draft: e.target.value })}
              rows={4}
              autoFocus
            />
            <div className="cd-entry__actions">
              <button type="button" className="cd-btn cd-btn--primary" disabled={busy} onClick={handleSaveEdit}>Save</button>
              <button type="button" className="cd-btn" disabled={busy} onClick={() => setEditing(null)}>Cancel</button>
            </div>
          </div>
        ) : (
          <>
            <div className="cd-entry__content">{entry.content}</div>
            {entry.has_citation && (entry.source_message_id || entry.source_session_id || entry.citation) && (
              <button
                type="button"
                className="cd-entry__prov"
                onClick={() => setExpandedProv((m) => ({ ...m, [entry.id]: !m[entry.id] }))}
              >
                1 source
                {expandedProv[entry.id] ? ' ▾' : ' ▸'}
              </button>
            )}
            {expandedProv[entry.id] && (
              <div className="cd-entry__prov-list">
                {entry.citation?.kind === 'import_chunk' ? (
                  <button
                    type="button"
                    className="cd-prov-tag cd-prov-tag--import"
                    onClick={() => openImportChunkCitation(
                      citationShortId(entry.citation.id),
                      entry.citation,
                    )}
                  >
                    import_chunk
                  </button>
                ) : (
                  <span className="cd-prov-tag">message</span>
                )}
              </div>
            )}
            {proposed && (
              <label className="pd-altitude">
                <span>Level</span>
                <select
                  value={entry.altitude || 'scene'}
                  onChange={(e) => setEntryAltitude(entry.id, e.target.value)}
                  aria-label="Classify this offer"
                >
                  {ALTITUDES.map((a) => (
                    <option key={a.id} value={a.id}>{a.label}</option>
                  ))}
                </select>
              </label>
            )}
            <div className="cd-entry__actions">
              {proposed && (
                <>
                  <button type="button" className="cd-btn cd-btn--primary" disabled={busy} onClick={() => handleConfirm(entry)}>
                    {CONFIRM_TURN}
                  </button>
                  <button type="button" className="cd-btn" disabled={busy || generating} onClick={() => runExtract('regenerate_slot', entry.id)}>
                    Regenerate
                  </button>
                  <button type="button" className="cd-btn" disabled={busy} onClick={() => handleDiscard([entry.id])}>
                    Discard
                  </button>
                </>
              )}
              <button
                type="button"
                className="cd-btn"
                disabled={busy}
                onClick={() => setEditing({ id: entry.id, draft: entry.content || '' })}
              >
                Edit
              </button>
              {entry.collision && !proposed && (
                <>
                  <button type="button" className="cd-btn" disabled={busy} onClick={() => setAssigning(entry.id)}>
                    Move
                  </button>
                  <button type="button" className="cd-btn" disabled={busy} onClick={() => handleDelete(entry.id)}>
                    Delete
                  </button>
                </>
              )}
            </div>
            {assigning === entry.id && (
              <div className="pd-assign">
                <select
                  aria-label="Assign target"
                  defaultValue=""
                  onChange={(e) => {
                    const val = e.target.value;
                    if (!val) return;
                    if (ALTITUDES.some((a) => a.id === val)) handleAssign(entry.id, val, null);
                    else handleAssign(entry.id, entry.altitude, val);
                  }}
                >
                  <option value="">Assign to a level or target</option>
                  {ALTITUDES.map((a) => (
                    <option key={a.id} value={a.id}>{a.label}</option>
                  ))}
                  {targets.map((t) => (
                    <option key={t.id} value={t.id}>
                      {altitudeLabel(t.kind)}: {t.display_name || t.id}
                    </option>
                  ))}
                </select>
                <button type="button" className="cd-btn" onClick={() => setAssigning(null)}>Cancel</button>
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  const renderList = (entries) => {
    const live = entries.filter((e) => !e.discarded);
    const ordered = [...live].sort((a, b) => {
      if (a.status !== b.status) return a.status === 'proposed' ? -1 : 1;
      return (a.sequence ?? 999) - (b.sequence ?? 999);
    });
    if (!ordered.length) {
      return <div className="cd-half__empty">No entries on this thread yet.</div>;
    }
    return ordered.map((e) => renderEntry(e));
  };

  const renderHierarchy = () => {
    const acts = data?.acts || [];
    const byParent = {};
    confirmedAll.forEach((e) => {
      const key = e.outline_section_id || '__loose';
      (byParent[key] = byParent[key] || []).push(e);
    });
    return (
      <div className="pd-hierarchy" aria-label="Plot dossier by hierarchy">
        {acts.map((act) => (
          <section key={act.id} className="pd-hierarchy__act">
            <h4 className="pd-grid__head">{act.display_name || `Act ${act.sequence_order}`}</h4>
            {(byParent[act.id] || []).map((e) => renderEntry(e))}
          </section>
        ))}
        {(byParent.__loose || []).map((e) => renderEntry(e))}
      </div>
    );
  };

  if (data === undefined) {
    return (
      <div
        className="cd-root cd-root--page cd-state pd-root"
        style={{ '--pd-zoom': PD_ZOOM.default / 100 }}
      >
        Loading plot dossier…
      </div>
    );
  }

  const hasAnyEntry = threads.some((t) => (t.entries || []).some((e) => !e.discarded));
  const hasAnyIncludingDiscarded = threads.some((t) => (t.entries || []).length > 0);
  const auditQuestions = coerceAuditQuestions(data.audit?.questions);

  const logline = liftedLogline(data);
  const chatLines = unusedChatLines(data);
  const progressLine = outlineProgressLine(data?.outline_progress);

  const renderChatLineChoice = () => {
    if (!pickingTurn) return null;
    if (!chatLines.length) {
      return (
        <div className="pd-unfinished" role="status">
          <p className="pd-generate-why">{bringTurnEmptyWhy(data)}</p>
          <div className="cd-entry__actions">
            <button type="button" className="cd-btn" onClick={() => setPickingTurn(false)}>
              Not now
            </button>
          </div>
        </div>
      );
    }
    return (
      <div className="pd-chat-pick" role="region" aria-label={CHOOSE_A_CHAT_LINE}>
        <p className="pd-generate-why">{CHOOSE_A_CHAT_LINE}</p>
        <ul className="pd-chat-lines" aria-label="Unused lines from the chat">
          {chatLines.map((line) => (
            <li key={line.id}>
              <button
                type="button"
                className="cd-btn pd-chat-line"
                disabled={generating}
                onClick={() => pickChatLine(line.id)}
              >
                {line.content}
              </button>
            </li>
          ))}
        </ul>
        <div className="cd-entry__actions">
          <button type="button" className="cd-btn" onClick={() => setPickingTurn(false)}>
            Not now
          </button>
        </div>
      </div>
    );
  };

  const writeupBody = (
    <>
      <section className="pd-logline" aria-label="Logline">
        <div className="cd-half__head">
          <h3 className="cd-half__title">Logline</h3>
        </div>
        {logline ? (
          <p className="pd-logline__text">{logline}</p>
        ) : (
          <p className="cd-half__empty">{LOGLINE_EMPTY}</p>
        )}
      </section>

      {progressLine ? (
        <p className="pd-generate-why" data-testid="pd-outline-progress">
          {progressLine}
        </p>
      ) : null}

      {(data.gaps || []).length > 0 && (
        <div className="pd-unfinished" role="status">
          <strong>Gaps detected</strong>
          <ul className="pd-audit__qs">
            {data.gaps.map((g, i) => (
              <li key={`${g.kind}-${g.beat_name || g.after_entry_id || i}`}>{g.label || g.kind}</li>
            ))}
          </ul>
        </div>
      )}

      {(unclean.length > 0 || data.mapping_status === 'needs_resolution') && (
        <div className="pd-unfinished" role="status">
          <strong>Mapping is not clean.</strong>
          {' '}Assign this entry to a level or target
          {(missingAct || missingSequence) ? ", or open a session about the book's argument — an act or sequence is missing." : '.'}
          <div className="cd-entry__actions">
            {unclean[0] && (
              <button type="button" className="cd-btn" onClick={() => setAssigning(unclean[0].id)}>
                Assign this entry
              </button>
            )}
            {(missingAct || missingSequence) && (
              <button type="button" className="cd-btn cd-btn--primary" disabled={auditBusy} onClick={handleOpenFocus}>
                Open a session about the book&apos;s argument
              </button>
            )}
          </div>
        </div>
      )}

      {collisions.length > 0 && (
        <div className="pd-unfinished" role="status">
          An Act or Sequence collision got through. Move, delete, or edit the flagged entry.
        </div>
      )}

      {proposedHere.length > 0 && (
        <div className="pd-unfinished" role="status">
          {PENDING_TURNS_WHY}
        </div>
      )}

      <div className="pd-threads" role="tablist" aria-label="Plot threads">
        {threads.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={activeThread?.id === t.id}
            className={`bs-view-toggle__btn ${activeThread?.id === t.id ? 'bs-view-toggle__btn--active' : ''}`}
            onClick={() => setActiveThreadId(t.id)}
          >
            {t.name || (t.kind === 'main' ? 'Main plot' : 'Subplot')}
          </button>
        ))}
        {addingSubplot ? (
          <span className="pd-add-thread">
            <input
              className="bs-edit__input"
              value={subplotName}
              onChange={(e) => setSubplotName(e.target.value)}
              placeholder="Subplot name"
              aria-label="Subplot name"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleAddSubplot();
                if (e.key === 'Escape') { setAddingSubplot(false); setSubplotName(''); }
              }}
            />
            <button type="button" className="cd-btn cd-btn--primary" disabled={busy} onClick={handleAddSubplot}>Add</button>
          </span>
        ) : (
          <button type="button" className="bs-add-link" onClick={() => setAddingSubplot(true)}>
            + Add subplot thread
          </button>
        )}
      </div>

      {!hasAnyEntry && !discardedHere.length ? (
        <div className="bs-empty">
          <AtlasIcon name="compendium" size={48} className="bs-empty__mark" />
          {generatedEmpty && !pickingTurn ? (
            <>
              <h2 className="bs-empty__heading">Nothing to bring yet</h2>
              <p className="bs-empty__lede">{bringTurnEmptyWhy(data)}</p>
            </>
          ) : (
            <>
              <h2 className="bs-empty__heading">Your notes are still in the chat.</h2>
              <p className="bs-empty__lede">{BRING_A_TURN_WHY}</p>
            </>
          )}
          {renderChatLineChoice()}
          <label className="pd-gapfill">
            <input
              type="checkbox"
              checked={!!data.gap_fill_enabled}
              onChange={async (e) => {
                await plotDossiersApi.setGapFill(storyId, e.target.checked);
                await load();
              }}
            />
            Allow gap-fill before Generate (invented entries stay marked invented)
          </label>
          <button type="button" className="bs-btn bs-btn--primary" disabled={generating} onClick={openBringChoice}>
            {generating ? 'Bringing a turn…' : generatedEmpty ? 'Try again' : BRING_A_TURN}
          </button>
        </div>
      ) : (
        <section className="cd-half">
          <div className="cd-half__head">
            <div className="cd-field__label">{activeThread?.name || 'Main plot'}</div>
            <button type="button" className="cd-btn" disabled={generating} onClick={openBringChoice}>
              {generating ? 'Bringing a turn…' : BRING_A_TURN}
            </button>
            <label className="pd-gapfill pd-gapfill--inline">
              <input
                type="checkbox"
                checked={!!data.gap_fill_enabled}
                onChange={async (e) => {
                  await plotDossiersApi.setGapFill(storyId, e.target.checked);
                  setData((d) => ({ ...d, gap_fill_enabled: e.target.checked }));
                }}
              />
              Gap-fill
            </label>
          </div>
          {renderChatLineChoice()}
          {proposedHere.length > 0 && (
            <div className="pd-offer-row" role="toolbar" aria-label="Pending turn actions">
              <button type="button" className="cd-btn" disabled={busy || !selectedIds.size} onClick={() => handleDiscard([...selectedIds])}>
                Discard selected
              </button>
              <button type="button" className="cd-btn" disabled={busy} onClick={handleDiscardAll}>
                {discardAllConfirm ? 'Confirm discard all' : 'Discard all'}
              </button>
              <button
                type="button"
                className="cd-btn"
                disabled={busy || generating || !discardedHere.length}
                onClick={() => runExtract('regenerate_discarded')}
              >
                Regenerate discarded slots
              </button>
            </div>
          )}
          {structureLinked ? renderHierarchy() : renderList(activeThread?.entries || [])}
          {discardedHere.length > 0 && !proposedHere.length && (
            <div className="pd-offer-row">
              <button type="button" className="cd-btn" disabled={generating} onClick={() => runExtract('regenerate_discarded')}>
                Regenerate discarded slots
              </button>
            </div>
          )}
        </section>
      )}
    </>
  );

  return (
    <div className="cd-root cd-root--page pd-root" style={{ '--pd-zoom': typeZoom / 100 }}>
      {paidDialog}
      <div className="cd-toolbar">
        <span className="cd-toolbar__title">The plot</span>
        <div className="pd-toolbar-actions">
          <TextSizeStepper
            value={typeZoom}
            onChange={setTypeZoom}
            min={PD_ZOOM.min}
            max={PD_ZOOM.max}
            step={PD_ZOOM.step}
            label="Plot text size"
          />
          {hasAnyIncludingDiscarded && (
            <>
              <button type="button" className="cd-btn cd-btn--ghost" disabled={busy || generating} onClick={openBringChoice}>
                {generating ? 'Proposing…' : PROPOSE_TURNS}
              </button>
              <button type="button" className="cd-btn cd-btn--ghost" disabled={busy || generating} onClick={() => runExtract('regenerate_all')}>
                {REGENERATE_ALL_TURNS}
              </button>
            </>
          )}
        </div>
      </div>

      {error && <div className="bs-error">{error}</div>}

      <section className="pd-writeup" aria-label="The plot">
        {writeupBody}
      </section>

      <section className="pd-framework" aria-label="Framework">
        <div className="cd-half__head">
          <h3 className="cd-half__title">Framework</h3>
          <div className="pd-framework__actions">
            <button type="button" className="cd-btn" onClick={() => setFrameworkPickerOpen(true)}>
              {frameworkLabel || (data.framework_ref ? data.framework_ref : 'Choose a Framework')}
            </button>
            <button type="button" className="cd-btn cd-btn--ghost" disabled={busy} onClick={handleFrameworkNone}>
              None
            </button>
          </div>
        </div>
        <GenreStandingMark genre={data.framework_ref ? frameworkGenre : null} />
        <ThinGenreMarker show={Boolean(data.framework_ref && frameworkThin)} />
        <p className="cd-half__empty">
          {data.framework_ref
            ? DOSSIER_FRAMEWORK_HAVE
            : 'No Framework. Writer order only — no overlay, no silent baseline.'}
        </p>
        {data.framework_drift && (
          <div className="pd-unfinished" role="status">
            Framework changed after entries existed. Regenerate against the new
            framework, or leave the entries as they are.
            <div className="cd-entry__actions">
              <button type="button" className="cd-btn" disabled={generating} onClick={() => runExtract('regenerate_all')}>
                Regenerate against framework
              </button>
              <button type="button" className="cd-btn" onClick={async () => {
                await plotDossiersApi.dismissFrameworkDrift(storyId);
                await load();
              }}>
                Leave entries as-is
              </button>
            </div>
          </div>
        )}
      </section>

      <section className="pd-audit" aria-label="Plot audit">
        <div className="cd-half__head">
          <h3 className="cd-half__title">Audit</h3>
          <button type="button" className="cd-btn cd-btn--ghost" disabled={auditBusy} onClick={handleAudit}>
            {auditBusy ? 'Auditing…' : 'Run audit'}
          </button>
        </div>
        <p className="cd-half__empty">
          {DOSSIER_AUDIT_TIP}
        </p>
        {auditQuestions.length > 0 && (
          <ul className="pd-audit__qs">
            {auditQuestions.map((q) => (
              <li key={q}>{q}</li>
            ))}
          </ul>
        )}
        {data.audit?.missing_foundation && (
          <p className="pd-unfinished" role="status">Foundation is missing. Open a session about the book&apos;s argument instead of inventing acts.</p>
        )}
        {data.audit && (
          <button type="button" className="cd-btn cd-btn--primary" disabled={auditBusy} onClick={handleOpenFocus}>
            Open a session about the book&apos;s argument
          </button>
        )}
      </section>

      <FrameworkPicker
        isOpen={frameworkPickerOpen}
        onClose={() => setFrameworkPickerOpen(false)}
        mode="select"
        hangKind="book"
        selectLabel="Use this Framework"
        selectHint={DOSSIER_FRAMEWORK_SELECT_HINT}
        selectedId={parseStoryFrameworkRef(data.framework_ref)?.id || null}
        storyId={storyId}
        handmadeSequenceCount={handmadeSequenceCount(data?.sequences, data?.scenes)}
        onSelect={handleSelectFramework}
      />
    </div>
  );
}
